import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  X, ChevronRight, AlertTriangle, Link2, Flag, Calendar,
  TrendingUp, TrendingDown, Minus, ExternalLink, Layers,
  FileText, Target, Clock, DollarSign, Users, GitBranch,
  Map, ArrowUp, ArrowDown, ArrowRight, Shield, History,
  FolderKanban, FileCheck, MessageCircle, RefreshCw, Filter,
  MessageCircleQuestion, Info, Sparkles, Inbox
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useProductMode } from '../context/ProductModeContext';
import CommentsPanel from './CommentsPanel';
import ScopeGovernancePanel from './ScopeGovernancePanel';
import LinkedRelationshipsPanel from './LinkedRelationshipsPanel';
import GovernanceLinkSelector from './GovernanceLinkSelector';
import { useSupportContext } from '../context/SupportContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { trackInspectorOpen, trackInspectorTabClick } from '../utils/telemetry';
import { InlineErrorBoundary } from './ErrorBoundary';
import { toNumber, clamp } from '../utils/dataAdapters';

/**
 * DetailDrawerV2 - Enhanced right-side slide-out panel (560px width)
 * Features mini roadmap strip, lazy-loaded tabs with counts, and trend indicators
 * Updated with standardized InspectorContext support
 */
const DetailDrawerV2 = ({ 
  isOpen, 
  onClose, 
  type,
  data: dataProp,
  item: itemProp,
  title,
  currency = 'USD',
  showAuditLog = false,
  tenantId,
  // New: InspectorContext props
  context: inspectorContext
}) => {
  // Support both 'data' and 'item' props, plus new context
  const data = inspectorContext?.item || dataProp || itemProp;
  const entityType = inspectorContext?.entityType || (data?.type || type || 'work-item').toLowerCase();
  const entityId = inspectorContext?.entityId || data?.id;
  const dateRange = inspectorContext?.dateRange || null;
  const parentChain = inspectorContext?.parentChain || {};
  const initialLinkedTab = inspectorContext?.initialLinkedTab || 'risks';
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openSupportDrawer, setInspectorContext } = useSupportContext();
  const { isScopingOnly, isEntityTypeAllowed, isInspectorTabAllowed, canGenerateWorkItems, productMode } = useProductMode();
  
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  
  // PO View toggle - persisted in localStorage
  const [poViewEnabled, setPoViewEnabled] = useState(() => {
    try {
      return localStorage.getItem('sv:inspector:poView') === 'true';
    } catch {
      return false;
    }
  });
  
  // Toggle PO View with telemetry
  const togglePoView = () => {
    const newValue = !poViewEnabled;
    setPoViewEnabled(newValue);
    try {
      localStorage.setItem('sv:inspector:poView', newValue.toString());
    } catch {}
    // Track telemetry
    import('../utils/telemetry').then(({ trackEvent, TELEMETRY_EVENTS }) => {
      trackEvent(TELEMETRY_EVENTS.INSPECTOR_PO_VIEW_ENABLED, { enabled: newValue, entityType });
    });
  };
  
  // Check if entity is allowed in scoping mode
  const entityAllowed = useMemo(() => {
    if (!isScopingOnly) return true;
    return isEntityTypeAllowed(entityType);
  }, [isScopingOnly, entityType, isEntityTypeAllowed]);
  
  // Tab-specific data and counts (lazy loaded)
  const [tabData, setTabData] = useState({});
  const [tabCounts, setTabCounts] = useState({});
  const [tabLoading, setTabLoading] = useState({});
  const [tabErrors, setTabErrors] = useState({});
  
  // Governance Link Selector state
  const [linkSelectorOpen, setLinkSelectorOpen] = useState(false);
  const [linkSelectorContext, setLinkSelectorContext] = useState(null);

  useEffect(() => {
    if (isOpen && data) {
      setActiveTab('summary');
      setError(null);
      setTabData({});
      setTabCounts({});
      setTabErrors({});
      loadDetails();
      // Pre-fetch counts for all tabs
      loadTabCounts();
      // Track inspector open
      trackInspectorOpen(entityType, productMode);
    }
  }, [isOpen, entityId, entityType]);

  // Handle tab click with telemetry
  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    trackInspectorTabClick(tabId, entityType, productMode);
  };

  // Load tab-specific data when tab is selected
  useEffect(() => {
    if (isOpen && entityId && activeTab !== 'summary' && activeTab !== 'comments') {
      loadTabData(activeTab);
    }
  }, [activeTab, isOpen, entityId]);

  const loadDetails = async () => {
    if (!entityId) return;
    setLoading(true);
    setError(null);
    try {
      if (entityType === 'portfolio') {
        const res = await api.getPortfolioDashboard(entityId, {});
        setDetails(res.data);
      } else if (['epic', 'feature', 'program', 'project'].includes(entityType)) {
        const res = await api.getItemRollup(entityType, entityId);
        setDetails(res.data);
      } else if (entityType === 'story' || entityType === 'task') {
        setDetails(data);
      }
    } catch (err) {
      console.error('Failed to load details:', err);
      setError("We couldn't load the details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Load counts for all tabs to show in labels
  const loadTabCounts = async () => {
    if (!entityId) return;
    
    const params = buildQueryParams();
    
    try {
      // Fetch counts in parallel
      const [risksRes, issuesRes, depsRes, decisionsRes] = await Promise.all([
        api.getIssues({ ...params, type: 'Risk', limit: 1 }).catch(() => ({ data: { total: 0 } })),
        api.getIssues({ ...params, type: 'Issue', limit: 1 }).catch(() => ({ data: { total: 0 } })),
        api.getDependencies({ ...params, limit: 1 }).catch(() => ({ data: { total: 0 } })),
        api.getDecisions({ ...params, limit: 1 }).catch(() => ({ data: { total: 0 } }))
      ]);
      
      setTabCounts({
        risks: risksRes.data?.total || 0,
        issues: issuesRes.data?.total || 0,
        dependencies: depsRes.data?.total || 0,
        decisions: decisionsRes.data?.total || 0
      });
    } catch (err) {
      console.error('Failed to load tab counts:', err);
    }
  };

  // Build query params based on entity context
  const buildQueryParams = () => {
    const params = {};
    
    // Set context based on entity type
    if (entityType === 'portfolio') {
      params.portfolio_id = entityId;
    } else if (entityType === 'program') {
      params.program_id = entityId;
    } else if (entityType === 'project') {
      params.project_id = entityId;
    } else if (entityType === 'epic') {
      params.epic_id = entityId;
    } else if (entityType === 'feature') {
      params.feature_id = entityId;
    }
    
    // Add parent chain context for scoping
    if (parentChain?.portfolioId && !params.portfolio_id) params.portfolio_id = parentChain.portfolioId;
    if (parentChain?.programId && !params.program_id) params.program_id = parentChain.programId;
    if (parentChain?.projectId && !params.project_id) params.project_id = parentChain.projectId;
    
    // Add date range filter if available
    if (dateRange?.start && dateRange?.end) {
      params.from = dateRange.start;
      params.to = dateRange.end;
    }
    
    return params;
  };

  // Lazy load data for a specific tab
  const loadTabData = async (tabId) => {
    if (!entityId || tabData[tabId] || tabLoading[tabId]) return;
    
    setTabLoading(prev => ({ ...prev, [tabId]: true }));
    setTabErrors(prev => ({ ...prev, [tabId]: null }));
    
    const params = buildQueryParams();
    
    try {
      let response;
      
      switch (tabId) {
        case 'risks':
          response = await api.getIssues({ ...params, type: 'Risk', limit: 50 });
          setTabData(prev => ({ ...prev, risks: response.data?.issues || [] }));
          setTabCounts(prev => ({ ...prev, risks: response.data?.total || 0 }));
          break;
          
        case 'issues':
          response = await api.getIssues({ ...params, type: 'Issue', limit: 50 });
          setTabData(prev => ({ ...prev, issues: response.data?.issues || [] }));
          setTabCounts(prev => ({ ...prev, issues: response.data?.total || 0 }));
          break;
          
        case 'dependencies':
          response = await api.getDependencies({ ...params, limit: 50 });
          setTabData(prev => ({ ...prev, dependencies: response.data?.dependencies || [] }));
          setTabCounts(prev => ({ ...prev, dependencies: response.data?.total || 0 }));
          break;
          
        case 'decisions':
          response = await api.getDecisions({ ...params, limit: 50 });
          setTabData(prev => ({ ...prev, decisions: response.data?.decisions || [] }));
          setTabCounts(prev => ({ ...prev, decisions: response.data?.total || 0 }));
          break;
          
        case 'children':
          if (entityType === 'program') {
            response = await api.getProjects({ program_id: entityId });
            setTabData(prev => ({ ...prev, children: response.data?.projects || [] }));
          } else if (entityType === 'project') {
            response = await api.getEpics({ project_id: entityId });
            setTabData(prev => ({ ...prev, children: response.data?.epics || [] }));
          } else if (entityType === 'epic') {
            response = await api.getFeatures({ epic_id: entityId });
            setTabData(prev => ({ ...prev, children: response.data?.features || [] }));
          }
          break;
          
        case 'programs':
          response = await api.getPrograms({ portfolio_id: entityId });
          setTabData(prev => ({ ...prev, programs: response.data?.programs || [] }));
          break;
          
        default:
          break;
      }
    } catch (err) {
      console.error(`Failed to load ${tabId} data:`, err);
      setTabErrors(prev => ({ 
        ...prev, 
        [tabId]: err.response?.status === 403 
          ? "You don't have permission to view this data"
          : "Failed to load data. Please try again."
      }));
    } finally {
      setTabLoading(prev => ({ ...prev, [tabId]: false }));
    }
  };

  // Clear date filter
  const clearDateFilter = () => {
    // This would need to be propagated up - for now just reload
    loadTabData(activeTab);
  };

  // Tabs configuration based on entity type - now with counts and product mode filtering
  const getTabsForType = () => {
    let allTabs = [];
    
    if (entityType === 'portfolio') {
      allTabs = [
        { id: 'summary', label: 'Summary' },
        { id: 'programs', label: 'Programs' },
        { id: 'risks', label: 'Risks', count: tabCounts.risks },
        { id: 'issues', label: 'Issues', count: tabCounts.issues },
        { id: 'dependencies', label: 'Deps', count: tabCounts.dependencies },
        { id: 'decisions', label: 'Decisions', count: tabCounts.decisions },
        { id: 'roadmap', label: 'Roadmap' },
        { id: 'comments', label: 'Comments', icon: MessageCircle }
      ];
    } else {
      allTabs = [
        { id: 'summary', label: 'Summary' },
        { id: 'children', label: getChildrenLabel() },
        { id: 'risks', label: 'Risks', count: tabCounts.risks },
        { id: 'issues', label: 'Issues', count: tabCounts.issues },
        { id: 'dependencies', label: 'Deps', count: tabCounts.dependencies },
        { id: 'milestones', label: 'Milestones' },
        { id: 'roadmap', label: 'Roadmap' },
        { id: 'comments', label: 'Comments', icon: MessageCircle }
      ];
      
      // Add Decisions tab for program/project level
      if (['program', 'project'].includes(entityType)) {
        allTabs.splice(5, 0, { id: 'decisions', label: 'Decisions', count: tabCounts.decisions });
      }
      
      // Add Scope tab for work items with scope linkage
      if (data?.scope_origin_id || data?.baseline_version) {
        allTabs.splice(2, 0, { id: 'scope', label: 'Scope', icon: FileCheck });
      }
      
      // Add Governance tab for scope-related entities (Phase 1)
      if (['scope', 'scopeNode', 'scope_node', 'requirement'].includes(entityType)) {
        allTabs.push({ id: 'governance', label: 'Governance', icon: Shield });
      }
    }
    
    // Add audit log if requested
    if (showAuditLog) {
      allTabs.push({ id: 'audit', label: 'Audit Log' });
    }
    
    // Add Support AI tab in scoping mode
    if (isScopingOnly) {
      allTabs.push({ id: 'support', label: 'Ask AI', icon: Sparkles });
    }
    
    // Filter tabs based on product mode
    if (isScopingOnly) {
      let filtered = allTabs.filter(tab => isInspectorTabAllowed(tab.id));
      
      // PO View mode: emphasize certain tabs, de-emphasize/hide execution tabs
      if (poViewEnabled && !canGenerateWorkItems) {
        // In PO View with generateWorkItems=false, hide execution-focused tabs
        const executionTabs = ['children', 'roadmap', 'milestones'];
        filtered = filtered.filter(tab => !executionTabs.includes(tab.id));
      }
      
      return filtered;
    }
    
    // PO View in full mode - de-emphasize execution tabs but keep accessible
    if (poViewEnabled) {
      // Mark execution tabs as de-emphasized (for styling)
      const executionTabs = ['children', 'roadmap', 'milestones', 'risks', 'issues'];
      allTabs = allTabs.map(tab => ({
        ...tab,
        deEmphasized: executionTabs.includes(tab.id)
      }));
      
      // Move emphasized tabs (decisions, governance, comments) earlier
      const emphasizedTabs = ['summary', 'decisions', 'governance', 'scope', 'comments', 'support'];
      allTabs.sort((a, b) => {
        const aIdx = emphasizedTabs.indexOf(a.id);
        const bIdx = emphasizedTabs.indexOf(b.id);
        if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
        if (aIdx >= 0) return -1;
        if (bIdx >= 0) return 1;
        return 0;
      });
    }
    
    return allTabs;
  };
  
  // Get label for children tab based on entity type
  const getChildrenLabel = () => {
    switch (entityType) {
      case 'program': return 'Projects';
      case 'project': return 'Epics';
      case 'epic': return 'Features';
      case 'feature': return 'Stories';
      default: return 'Children';
    }
  };
  
  const tabs = getTabsForType();

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Status colors using semantic tokens
  const getStatusColor = (status) => ({
    'Blocked': 'bg-[var(--clr-sev-blocked)]',
    'Behind': 'bg-[var(--clr-sev-behind)]',
    'Due Date at Risk': 'bg-[var(--clr-sev-atrisk)]',
    'At Risk': 'bg-[var(--clr-sev-atrisk)]',
    'In Progress': 'bg-[var(--clr-sev-inprogress)]',
    'Completed': 'bg-[var(--clr-sev-completed)]',
    'Not Started': 'bg-[var(--clr-sev-notstarted)]'
  }[status] || 'bg-[var(--clr-sev-notstarted)]');

  const getHealthColor = (health) => ({
    'Red': 'text-[var(--clr-sev-blocked)] bg-[var(--clr-sev-blocked-bg)]',
    'Amber': 'text-[var(--clr-sev-atrisk)] bg-[var(--clr-sev-atrisk-bg)]',
    'Green': 'text-[var(--clr-sev-healthy)] bg-[var(--clr-sev-healthy-bg)]'
  }[health] || 'text-[var(--clr-text-muted)] bg-[var(--clr-surface-subtle)]');

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      
      <div 
        className={`fixed right-0 top-0 h-full w-[560px] bg-[var(--clr-surface-card)] shadow-[var(--shadow-drawer)] z-50 transform transition-transform duration-300 flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="detail-drawer-v2"
      >
        {/* Header with Mini Roadmap */}
        <div className="flex-shrink-0 border-b border-[var(--clr-border-light)]">
          <div className="flex items-center justify-between px-5 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-3 h-3 rounded-full ${getStatusColor(data?.status)}`} />
              <h2 className="text-lg font-semibold text-[var(--clr-text-primary)] truncate">{title}</h2>
            </div>
            <div className="flex items-center gap-2">
              {/* PO View Toggle - shown in scoping contexts */}
              {isScopingOnly && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button 
                        onClick={togglePoView}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          poViewEnabled 
                            ? 'bg-purple-100 text-purple-700 border border-purple-300' 
                            : 'text-gray-500 hover:bg-gray-100 border border-transparent'
                        }`}
                        data-testid="po-view-toggle"
                      >
                        <Target className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">PO</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p className="text-xs">
                        {poViewEnabled 
                          ? 'Product Owner view: Decisions, Docs, NFR focus' 
                          : 'Enable Product Owner view'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              
              {/* Ask Support AI button */}
              <button 
                onClick={() => {
                  setInspectorContext({
                    type: entityType,
                    id: entityId,
                    projectId: data?.project_id,
                    parentChain
                  });
                  openSupportDrawer({
                    mode: 'advisory',
                    entity: {
                      type: entityType,
                      id: entityId,
                      projectId: data?.project_id,
                      parentChain
                    }
                  });
                }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                title="Ask Support AI about this item"
                data-testid="inspector-support-ai-btn"
              >
                <MessageCircleQuestion className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Ask AI</span>
              </button>
              <button onClick={onClose} className="p-1.5 hover:bg-[var(--clr-surface-hover)] rounded-lg">
                <X className="w-5 h-5 text-[var(--clr-text-muted)]" />
              </button>
            </div>
          </div>

          {/* Mini Roadmap Strip */}
          {data && (data.start_date || data.due_date) && (
            <MiniRoadmapStrip
              startDate={data.start_date}
              dueDate={data.due_date}
              revisedDueDate={data.revised_due_date}
              percentComplete={data.percent_complete || details?.percent_complete || 0}
              status={data.status}
            />
          )}

          {/* Tabs with counts */}
          <div className="flex gap-1 px-4 py-2 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors flex items-center gap-1 ${
                  activeTab === tab.id
                    ? 'bg-[var(--clr-brand-primary)] text-white'
                    : tab.deEmphasized && poViewEnabled
                    ? 'text-[var(--clr-text-muted)] opacity-60 hover:bg-[var(--clr-surface-hover)] hover:opacity-100'
                    : 'text-[var(--clr-text-secondary)] hover:bg-[var(--clr-surface-hover)]'
                }`}
              >
                {tab.icon && <tab.icon className="w-3 h-3" />}
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 text-[10px] rounded-full ${
                    activeTab === tab.id 
                      ? 'bg-white/20 text-white' 
                      : 'bg-[var(--clr-brand-primary)]/10 text-[var(--clr-brand-primary)]'
                  }`}>
                    {tab.count}
                  </span>
                )}
                {tabLoading[tab.id] && (
                  <RefreshCw className="w-3 h-3 animate-spin ml-1" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div 
          className="flex-1 overflow-y-auto"
          data-testid="detail-drawer-content"
        >
          {/* Error toast */}
          {error && (
            <div className="mx-4 mt-4 p-3 bg-[var(--clr-sev-atrisk-bg)] border border-[var(--clr-sev-atrisk-border)] rounded-lg flex items-center gap-2 text-sm text-[var(--clr-sev-atrisk)]">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          
          {/* Not Available in Scoping Mode Panel */}
          {isScopingOnly && !entityAllowed && (
            <div className="p-5">
              <ScopingModeUnavailablePanel entityType={entityType} openSupportDrawer={openSupportDrawer} />
            </div>
          )}
          
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--clr-brand-primary)]" />
            </div>
          ) : entityAllowed && (
            <div className="p-5">
              {activeTab === 'summary' && (
                <>
                  <SummaryTab data={data} details={details} formatCurrency={formatCurrency} getHealthColor={getHealthColor} type={type} />
                  {/* Linked Relationships Panel - integrated into summary */}
                  <LinkedRelationshipsPanel
                    workId={entityId}
                    workType={data?.type || entityType}
                    entityType={entityType}
                    initialTab={initialLinkedTab}
                    autoScrollEnabled={true}
                    onViewArtifact={(artifact) => {
                      // TODO: Open artifact detail
                      console.log('View artifact:', artifact);
                    }}
                    onOpenSelector={(context) => {
                      setLinkSelectorContext(context);
                      setLinkSelectorOpen(true);
                    }}
                  />
                </>
              )}
              {activeTab === 'children' && (
                <ChildrenTab data={data} details={details} getStatusColor={getStatusColor} tabData={tabData.children} tabLoading={tabLoading.children} />
              )}
              {activeTab === 'scope' && (
                <ScopeTab data={data} tenantId={tenantId || user?.tenant?.id || user?.tenant_id} />
              )}
              {activeTab === 'programs' && (
                <ProgramsTab data={data} details={details} getStatusColor={getStatusColor} tabData={tabData.programs} tabLoading={tabLoading.programs} />
              )}
              {activeTab === 'risks' && (
                <RisksTab tabData={tabData.risks} tabLoading={tabLoading.risks} tabError={tabErrors.risks} dateRange={dateRange} onClearDateFilter={clearDateFilter} />
              )}
              {activeTab === 'issues' && (
                <IssuesTab tabData={tabData.issues} tabLoading={tabLoading.issues} tabError={tabErrors.issues} dateRange={dateRange} onClearDateFilter={clearDateFilter} />
              )}
              {activeTab === 'dependencies' && (
                <DependenciesTab tabData={tabData.dependencies} tabLoading={tabLoading.dependencies} tabError={tabErrors.dependencies} dateRange={dateRange} onClearDateFilter={clearDateFilter} />
              )}
              {activeTab === 'milestones' && (
                <MilestonesTab data={data} details={details} />
              )}
              {activeTab === 'decisions' && (
                <DecisionsTabV2 tabData={tabData.decisions} tabLoading={tabLoading.decisions} tabError={tabErrors.decisions} dateRange={dateRange} onClearDateFilter={clearDateFilter} />
              )}
              {activeTab === 'roadmap' && (
                <RoadmapTab data={data} details={details} />
              )}
              {activeTab === 'audit' && showAuditLog && (
                <AuditLogTab data={data} details={details} type={type} />
              )}
              {activeTab === 'comments' && (
                <CommentsTab 
                  data={data} 
                  type={type} 
                  tenantId={tenantId || user?.tenant?.id || user?.tenant_id} 
                />
              )}
              {activeTab === 'governance' && (
                <ScopeGovernancePanel 
                  scopeId={entityId}
                  scopeName={data?.name}
                  tenantId={tenantId || user?.tenant?.id || user?.tenant_id}
                />
              )}
              {activeTab === 'support' && (
                <SupportAITab entityType={entityType} entityId={entityId} data={data} openSupportDrawer={openSupportDrawer} />
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Governance Link Selector Modal */}
      <GovernanceLinkSelector
        isOpen={linkSelectorOpen}
        onClose={() => {
          setLinkSelectorOpen(false);
          setLinkSelectorContext(null);
        }}
        source="work"
        workId={entityId}
        workTitle={data?.name || data?.title}
        onLinkCreated={linkSelectorContext?.onLinkCreated}
        artifactType={linkSelectorContext?.artifactType}
        artifactId={linkSelectorContext?.artifactId}
        artifactTitle={linkSelectorContext?.artifactTitle}
      />
    </>
  );
};

// Mini Roadmap Strip Component
const MiniRoadmapStrip = ({ startDate, dueDate, revisedDueDate, percentComplete, status }) => {
  const now = new Date();
  const start = startDate ? new Date(startDate) : null;
  const due = dueDate ? new Date(dueDate) : null;
  const revised = revisedDueDate ? new Date(revisedDueDate) : null;

  if (!start || !due) return null;

  const totalDays = Math.max(1, (due - start) / (1000 * 60 * 60 * 24));
  const elapsedDays = Math.max(0, (now - start) / (1000 * 60 * 60 * 24));
  const progressPct = Math.min(100, (elapsedDays / totalDays) * 100);

  const statusColors = {
    'Blocked': 'bg-[var(--clr-sev-blocked)]',
    'Behind': 'bg-[var(--clr-sev-behind)]',
    'At Risk': 'bg-[var(--clr-sev-atrisk)]',
    'In Progress': 'bg-[var(--clr-sev-inprogress)]',
    'On Track': 'bg-[var(--clr-sev-healthy)]'
  };

  return (
    <div className="px-5 py-2 bg-[var(--clr-surface-page)] border-t">
      <div className="flex items-center justify-between text-xs text-[var(--clr-text-muted)] mb-1">
        <span>{start.toLocaleDateString()}</span>
        <span className="font-medium text-[var(--clr-text-secondary)]">{percentComplete?.toFixed(0)}% Complete</span>
        <span>{(revised || due).toLocaleDateString()}</span>
      </div>
      <div className="relative h-2 bg-[var(--clr-border-light)] rounded-full overflow-hidden">
        {/* Time elapsed bar */}
        <div
          className="absolute h-full bg-[var(--clr-border-medium)]"
          style={{ width: `${progressPct}%` }}
        />
        {/* Completion bar */}
        <div
          className={`absolute h-full ${statusColors[status] || 'bg-[var(--clr-brand-primary)] bg-opacity-100'} rounded-full`}
          style={{ width: `${percentComplete || 0}%` }}
        />
        {/* Today marker */}
        {progressPct > 0 && progressPct < 100 && (
          <div
            className="absolute top-0 w-0.5 h-full bg-slate-900"
            style={{ left: `${progressPct}%` }}
          />
        )}
      </div>
      {revised && revised > due && (
        <div className="text-xs text-[var(--clr-sev-atrisk)] mt-1">
          Revised: {revised.toLocaleDateString()} (+{Math.ceil((revised - due) / (1000 * 60 * 60 * 24))} days)
        </div>
      )}
    </div>
  );
};

// Summary Tab
const SummaryTab = ({ data, details, formatCurrency, getHealthColor }) => {
  const merged = { ...data, ...details };
  const health = merged.health || (
    merged.status === 'Blocked' || merged.status === 'Behind' ? 'Red' :
    merged.status === 'At Risk' ? 'Amber' : 'Green'
  );

  const TrendIndicator = ({ value, inverse = false }) => {
    if (value === undefined || value === null) return null;
    const Icon = value > 0 ? ArrowUp : value < 0 ? ArrowDown : ArrowRight;
    const color = value === 0 ? 'text-[var(--clr-text-muted)]' : 
      (inverse ? (value > 0 ? 'text-[var(--clr-sev-blocked)]' : 'text-[var(--clr-sev-healthy)]') : 
      (value > 0 ? 'text-[var(--clr-sev-healthy)]' : 'text-[var(--clr-sev-blocked)]'));
    return (
      <span className={`inline-flex items-center gap-0.5 ${color}`}>
        <Icon className="w-3 h-3" />
        <span className="text-xs">{Math.abs(value)}%</span>
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Health Status */}
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${getHealthColor(health)}`}>
        <Shield className="w-4 h-4" />
        <span className="font-medium">{health} - {merged.status}</span>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 gap-3">
        <MetricCard
          icon={<Target className="w-4 h-4 text-blue-500" />}
          label="% Complete"
          value={`${merged.percent_complete?.toFixed(1) || 0}%`}
          trend={<TrendIndicator value={merged.complete_trend} />}
        />
        <MetricCard
          icon={<Shield className="w-4 h-4 text-[var(--clr-sev-healthy)]" />}
          label="Confidence"
          value={`${merged.confidence || merged.delivery_confidence || 0}%`}
          trend={<TrendIndicator value={merged.confidence_trend} />}
        />
        <MetricCard
          icon={<DollarSign className="w-4 h-4 text-[var(--clr-text-muted)]" />}
          label="Est. Cost"
          value={formatCurrency(merged.est_cost || merged.estimated_cost)}
        />
        <MetricCard
          icon={<DollarSign className="w-4 h-4 text-[var(--clr-sev-atrisk)]" />}
          label="Variance"
          value={formatCurrency(merged.variance || merged.cost_variance)}
        />
      </div>

      {/* Timeline */}
      <div className="bg-[var(--clr-surface-page)] rounded-lg p-3">
        <h4 className="text-xs font-medium text-[var(--clr-text-muted)] mb-2">Timeline</h4>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <div>
            <div className="text-[var(--clr-text-muted)] text-xs">Start</div>
            <div className="font-medium">{merged.start_date ? new Date(merged.start_date).toLocaleDateString() : '-'}</div>
          </div>
          <div>
            <div className="text-[var(--clr-text-muted)] text-xs">Due</div>
            <div className="font-medium">{merged.due_date ? new Date(merged.due_date).toLocaleDateString() : '-'}</div>
          </div>
          <div>
            <div className="text-[var(--clr-text-muted)] text-xs">Revised</div>
            <div className="font-medium">{merged.revised_due_date ? new Date(merged.revised_due_date).toLocaleDateString() : '-'}</div>
          </div>
        </div>
      </div>

      {/* Parent/Owner Info */}
      <div className="space-y-2">
        {merged.program_name && (
          <div className="flex items-center gap-2 text-sm">
            <Layers className="w-4 h-4 text-[var(--clr-text-muted)]" />
            <span className="text-[var(--clr-text-muted)]">Program:</span>
            <span className="font-medium">{merged.program_name}</span>
          </div>
        )}
        {merged.project_name && (
          <div className="flex items-center gap-2 text-sm">
            <FileText className="w-4 h-4 text-[var(--clr-text-muted)]" />
            <span className="text-[var(--clr-text-muted)]">Project:</span>
            <span className="font-medium">{merged.project_name}</span>
          </div>
        )}
        {(merged.owner || merged.project_manager) && (
          <div className="flex items-center gap-2 text-sm">
            <Users className="w-4 h-4 text-[var(--clr-text-muted)]" />
            <span className="text-[var(--clr-text-muted)]">Owner:</span>
            <span className="font-medium">{merged.owner || merged.project_manager}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const MetricCard = ({ icon, label, value, trend }) => (
  <div className="bg-[var(--clr-surface-page)] rounded-lg p-3">
    <div className="flex items-center justify-between mb-1">
      {icon}
      {trend}
    </div>
    <div className="text-lg font-bold text-[var(--clr-text-primary)]">{value}</div>
    <div className="text-xs text-[var(--clr-text-muted)]">{label}</div>
  </div>
);

// Children Tab
const ChildrenTab = ({ data, details, getStatusColor }) => {
  const children = details?.children || data?.children || data?.epics || [];
  
  if (children.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--clr-text-muted)]">
        <Layers className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>No child items</p>
      </div>
    );
  }

  // Sort by severity
  const statusOrder = { 'Blocked': 0, 'Behind': 1, 'At Risk': 2, 'In Progress': 3, 'Completed': 4 };
  const sorted = [...children].sort((a, b) => 
    (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99)
  );

  return (
    <div className="space-y-2">
      {sorted.map((child, idx) => (
        <div key={child.id || idx} className="flex items-center gap-3 p-3 bg-[var(--clr-surface-page)] rounded-lg">
          <div className={`w-2 h-2 rounded-full ${getStatusColor(child.status)}`} />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-[var(--clr-text-primary)] truncate">{child.name}</div>
            <div className="text-xs text-[var(--clr-text-muted)]">{child.type || 'Item'}</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-16 h-1.5 bg-[var(--clr-border-light)] rounded-full overflow-hidden">
              <div
                className="h-full bg-[var(--clr-brand-primary)] bg-opacity-100 rounded-full"
                style={{ width: `${child.percent_complete || 0}%` }}
              />
            </div>
            <span className="text-xs text-[var(--clr-text-secondary)] w-8">{child.percent_complete?.toFixed(0) || 0}%</span>
          </div>
        </div>
      ))}
    </div>
  );
};

// Programs Tab (for Portfolio detail)
const ProgramsTab = ({ data, details, getStatusColor }) => {
  const programs = details?.program_roadmap || details?.programs || data?.programs || [];
  
  if (programs.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--clr-text-muted)]">
        <FolderKanban className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>No programs in this portfolio</p>
      </div>
    );
  }

  const statusOrder = { 'Blocked': 0, 'Behind': 1, 'At Risk': 2, 'In Progress': 3, 'On Track': 4, 'Completed': 5 };
  const sorted = [...programs].sort((a, b) => 
    (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99)
  );

  return (
    <div className="space-y-3">
      {sorted.map((program, idx) => (
        <div key={program.id || idx} className="p-3 bg-[var(--clr-surface-page)] rounded-lg border-l-3" style={{ borderLeftWidth: '3px', borderLeftColor: getStatusColor(program.status)?.includes('blocked') ? 'var(--clr-sev-blocked)' : getStatusColor(program.status)?.includes('atrisk') ? 'var(--clr-sev-atrisk)' : 'var(--clr-sev-healthy)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-[var(--clr-text-primary)]">{program.name}</span>
            <span className={`px-2 py-0.5 text-xs rounded-full text-white ${getStatusColor(program.status)}`}>
              {program.status || 'Unknown'}
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs text-[var(--clr-text-muted)]">
            <span>{program.project_count || 0} projects</span>
            <span>{program.risk_count || 0} risks</span>
            <span className="ml-auto font-medium text-[var(--clr-text-secondary)]">{program.percent_complete || 0}%</span>
          </div>
          <div className="mt-2 w-full h-1.5 bg-[var(--clr-border-light)] rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--clr-brand-primary)] rounded-full"
              style={{ width: `${program.percent_complete || 0}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// Decisions Tab - Uses lazy-loaded data
const DecisionsTabV2 = ({ tabData, tabLoading, tabError, dateRange, onClearDateFilter }) => {
  const decisions = tabData || [];
  
  if (tabLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-[var(--clr-text-muted)]">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" />
        <span>Loading decisions...</span>
      </div>
    );
  }
  
  if (tabError) {
    return (
      <div className="text-center py-8">
        <FileCheck className="w-8 h-8 mx-auto mb-2 text-[var(--clr-sev-atrisk)]" />
        <p className="text-[var(--clr-text-secondary)]">{tabError}</p>
      </div>
    );
  }
  
  if (decisions.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--clr-text-muted)]">
        <FileCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>No decisions found in this period</p>
        {dateRange && (
          <button 
            onClick={onClearDateFilter}
            className="mt-2 text-xs text-[var(--clr-brand-primary)] hover:underline flex items-center gap-1 mx-auto"
          >
            <Filter className="w-3 h-3" />
            Clear date filter
          </button>
        )}
      </div>
    );
  }

  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return { bg: 'bg-[var(--clr-sev-healthy-bg)]', text: 'text-[var(--clr-sev-healthy)]' };
      case 'rejected': return { bg: 'bg-[var(--clr-sev-blocked-bg)]', text: 'text-[var(--clr-sev-blocked)]' };
      default: return { bg: 'bg-[var(--clr-sev-atrisk-bg)]', text: 'text-[var(--clr-sev-atrisk)]' };
    }
  };

  return (
    <div className="space-y-2">
      {decisions.map((decision, idx) => {
        const statusConfig = getStatusConfig(decision.status);
        return (
          <div key={decision.id || idx} className="p-3 bg-[var(--clr-surface-page)] rounded-lg border-l-3" style={{ borderLeftWidth: '3px', borderLeftColor: `var(--clr-sev-${decision.status === 'Approved' ? 'healthy' : decision.status === 'Rejected' ? 'blocked' : 'atrisk'})` }}>
            <div className="flex items-start justify-between mb-1">
              <span className="font-medium text-[var(--clr-text-primary)] pr-2">{decision.title || decision.decision}</span>
              <span className={`px-2 py-0.5 text-xs rounded-full flex-shrink-0 ${statusConfig.bg} ${statusConfig.text}`}>
                {decision.status}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[var(--clr-text-muted)]">
              {decision.request_type && <span className="px-1.5 py-0.5 bg-[var(--clr-surface-subtle)] rounded">{decision.request_type}</span>}
              {decision.request_date && <span>Requested: {new Date(decision.request_date).toLocaleDateString()}</span>}
              {decision.project_name && <span>{decision.project_name}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Risks Tab - Uses lazy-loaded data
const RisksTab = ({ tabData, tabLoading, tabError, dateRange, onClearDateFilter }) => {
  const risks = tabData || [];
  
  if (tabLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-[var(--clr-text-muted)]">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" />
        <span>Loading risks...</span>
      </div>
    );
  }
  
  if (tabError) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-[var(--clr-sev-atrisk)]" />
        <p className="text-[var(--clr-text-secondary)]">{tabError}</p>
      </div>
    );
  }
  
  if (risks.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--clr-text-muted)]">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>No risks found in this period</p>
        {dateRange && (
          <button 
            onClick={onClearDateFilter}
            className="mt-2 text-xs text-[var(--clr-brand-primary)] hover:underline flex items-center gap-1 mx-auto"
          >
            <Filter className="w-3 h-3" />
            Clear date filter
          </button>
        )}
      </div>
    );
  }

  const severityColors = {
    'Critical': 'bg-[var(--clr-sev-blocked)]',
    'High': 'bg-[var(--clr-sev-atrisk)]',
    'Medium': 'bg-[var(--clr-badge-priority-medium)]',
    'Low': 'bg-[var(--clr-sev-notstarted)]'
  };

  return (
    <div className="space-y-2">
      {risks.map((risk, idx) => (
        <div key={risk.id || idx} className="p-3 bg-[var(--clr-surface-page)] rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-1.5 h-5 rounded-full ${severityColors[risk.severity] || 'bg-slate-300'}`} />
            <span className="font-medium text-[var(--clr-text-primary)]">{risk.name || risk.title}</span>
            <span className={`ml-auto px-2 py-0.5 text-xs rounded ${
              risk.severity === 'Critical' ? 'bg-[var(--clr-sev-blocked-bg)] text-[var(--clr-sev-blocked)]' :
              risk.severity === 'High' ? 'bg-[var(--clr-sev-atrisk-bg)] text-[var(--clr-sev-atrisk)]' :
              'bg-[var(--clr-surface-subtle)] text-[var(--clr-text-secondary)]'
            }`}>{risk.severity}</span>
          </div>
          {risk.description && <p className="text-sm text-[var(--clr-text-secondary)] ml-3.5">{risk.description}</p>}
          <div className="flex items-center gap-2 mt-2 text-xs text-[var(--clr-text-muted)] ml-3.5">
            {risk.status && <span className="px-1.5 py-0.5 bg-[var(--clr-surface-subtle)] rounded">{risk.status}</span>}
            {risk.project_name && <span>{risk.project_name}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

// Issues Tab - Uses lazy-loaded data
const IssuesTab = ({ tabData, tabLoading, tabError, dateRange, onClearDateFilter }) => {
  const issues = tabData || [];
  
  if (tabLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-[var(--clr-text-muted)]">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" />
        <span>Loading issues...</span>
      </div>
    );
  }
  
  if (tabError) {
    return (
      <div className="text-center py-8">
        <Flag className="w-8 h-8 mx-auto mb-2 text-[var(--clr-sev-atrisk)]" />
        <p className="text-[var(--clr-text-secondary)]">{tabError}</p>
      </div>
    );
  }
  
  if (issues.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--clr-text-muted)]">
        <Flag className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>No issues found in this period</p>
        {dateRange && (
          <button 
            onClick={onClearDateFilter}
            className="mt-2 text-xs text-[var(--clr-brand-primary)] hover:underline flex items-center gap-1 mx-auto"
          >
            <Filter className="w-3 h-3" />
            Clear date filter
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {issues.map((issue, idx) => (
        <div key={issue.id || idx} className="p-3 bg-[var(--clr-surface-page)] rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Flag className="w-4 h-4 text-orange-500" />
            <span className="font-medium text-[var(--clr-text-primary)]">{issue.name || issue.title}</span>
            <span className={`ml-auto px-2 py-0.5 text-xs rounded ${
              issue.severity === 'Critical' ? 'bg-[var(--clr-sev-blocked-bg)] text-[var(--clr-sev-blocked)]' :
              issue.severity === 'High' ? 'bg-[var(--clr-sev-atrisk-bg)] text-[var(--clr-sev-atrisk)]' :
              'bg-[var(--clr-surface-subtle)] text-[var(--clr-text-secondary)]'
            }`}>{issue.severity || issue.priority}</span>
          </div>
          {issue.description && <p className="text-sm text-[var(--clr-text-secondary)]">{issue.description}</p>}
          <div className="flex items-center gap-2 mt-2 text-xs text-[var(--clr-text-muted)]">
            {issue.status && <span className="px-1.5 py-0.5 bg-[var(--clr-surface-subtle)] rounded">{issue.status}</span>}
            {issue.project_name && <span>{issue.project_name}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

// Dependencies Tab - Uses lazy-loaded data
const DependenciesTab = ({ tabData, tabLoading, tabError, dateRange, onClearDateFilter }) => {
  const deps = tabData || [];
  
  if (tabLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-[var(--clr-text-muted)]">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" />
        <span>Loading dependencies...</span>
      </div>
    );
  }
  
  if (tabError) {
    return (
      <div className="text-center py-8">
        <Link2 className="w-8 h-8 mx-auto mb-2 text-[var(--clr-sev-atrisk)]" />
        <p className="text-[var(--clr-text-secondary)]">{tabError}</p>
      </div>
    );
  }
  
  if (deps.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--clr-text-muted)]">
        <Link2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>No dependencies found</p>
        {dateRange && (
          <button 
            onClick={onClearDateFilter}
            className="mt-2 text-xs text-[var(--clr-brand-primary)] hover:underline flex items-center gap-1 mx-auto"
          >
            <Filter className="w-3 h-3" />
            Clear date filter
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {deps.map((dep, idx) => (
        <div key={dep.id || idx} className="p-3 bg-[var(--clr-surface-page)] rounded-lg">
          <div className="flex items-center gap-2">
            <Link2 className={`w-4 h-4 ${
              dep.status === 'Blocked' ? 'text-red-500' :
              dep.status === 'At Risk' ? 'text-orange-500' :
              'text-blue-500'
            }`} />
            <span className="font-medium text-[var(--clr-text-primary)] flex-1">{dep.description || dep.name}</span>
            {dep.status && (
              <span className={`px-2 py-0.5 text-xs rounded ${
                dep.status === 'Blocked' ? 'bg-[var(--clr-sev-blocked-bg)] text-[var(--clr-sev-blocked)]' :
                dep.status === 'At Risk' ? 'bg-[var(--clr-sev-atrisk-bg)] text-[var(--clr-sev-atrisk)]' :
                'bg-[var(--clr-surface-subtle)] text-[var(--clr-text-secondary)]'
              }`}>{dep.status}</span>
            )}
          </div>
          <div className="mt-2 text-xs text-[var(--clr-text-muted)] flex items-center gap-2">
            {dep.source_display && (
              <span className="flex items-center gap-1">
                <ArrowRight className="w-3 h-3" />
                {dep.source_display}
              </span>
            )}
            {dep.target_display && (
              <span className="flex items-center gap-1">
                <ArrowRight className="w-3 h-3" />
                {dep.target_display}
              </span>
            )}
            {dep.kind && <span className="px-1.5 py-0.5 bg-[var(--clr-surface-subtle)] rounded">{dep.kind}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

// Milestones Tab
const MilestonesTab = ({ data, details }) => {
  const milestones = details?.milestones || data?.milestones || [];
  
  if (milestones.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--clr-text-muted)]">
        <Calendar className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>No milestones</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {milestones.map((ms, idx) => {
        const dueDate = ms.due_date ? new Date(ms.due_date) : null;
        const isOverdue = dueDate && dueDate < new Date();
        
        return (
          <div key={ms.id || idx} className={`p-3 rounded-lg ${isOverdue ? 'bg-[var(--clr-sev-blocked)] bg-opacity-10' : 'bg-[var(--clr-surface-page)]'}`}>
            <div className="flex items-center gap-2">
              <Calendar className={`w-4 h-4 ${isOverdue ? 'text-[var(--clr-sev-blocked)]' : 'text-[var(--clr-sev-healthy)]'}`} />
              <span className="font-medium text-[var(--clr-text-primary)]">{ms.name}</span>
            </div>
            {dueDate && (
              <p className={`text-sm ml-6 ${isOverdue ? 'text-[var(--clr-sev-blocked)]' : 'text-[var(--clr-text-muted)]'}`}>
                {dueDate.toLocaleDateString()} {isOverdue && '(Overdue)'}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Roadmap Tab (Mini View)
const RoadmapTab = ({ data, details }) => {
  const children = details?.children || data?.children || [];
  
  if (children.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--clr-text-muted)]">
        <Map className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>No roadmap data available</p>
        <a href="/roadmap" className="text-sm text-[var(--clr-brand-primary)] hover:underline mt-2 inline-block">
          Open Full Roadmap →
        </a>
      </div>
    );
  }

  // Simple 6-month view
  const now = new Date();
  const startMonth = now.getMonth();
  const months = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), startMonth + i, 1);
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  });

  return (
    <div className="space-y-4">
      {/* Month Headers */}
      <div className="flex border-b pb-1">
        <div className="w-24 flex-shrink-0" />
        {months.map((m, i) => (
          <div key={i} className="flex-1 text-xs text-[var(--clr-text-muted)] text-center">{m}</div>
        ))}
      </div>

      {/* Items */}
      {children.slice(0, 8).map((item, idx) => {
        const start = item.start_date ? new Date(item.start_date) : now;
        const end = item.due_date ? new Date(item.due_date) : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
        
        // Calculate bar position (simplified)
        const totalDays = 180;
        const startOffset = Math.max(0, Math.min(100, ((start - now) / (totalDays * 24 * 60 * 60 * 1000)) * 100));
        const duration = Math.max(5, Math.min(100 - startOffset, ((end - start) / (totalDays * 24 * 60 * 60 * 1000)) * 100));

        const statusColors = {
          'Blocked': 'bg-[var(--clr-sev-blocked)]',
          'Behind': 'bg-[var(--clr-sev-behind)]',
          'At Risk': 'bg-[var(--clr-sev-atrisk)]',
          'In Progress': 'bg-[var(--clr-sev-inprogress)]',
          'Completed': 'bg-[var(--clr-sev-completed)]'
        };

        return (
          <div key={item.id || idx} className="flex items-center">
            <div className="w-24 flex-shrink-0 text-xs text-[var(--clr-text-secondary)] truncate pr-2">{item.name}</div>
            <div className="flex-1 h-4 bg-[var(--clr-surface-subtle)] rounded relative">
              <div
                className={`absolute h-full rounded ${statusColors[item.status] || 'bg-[var(--clr-brand-primary)] bg-opacity-100'}`}
                style={{ left: `${startOffset}%`, width: `${duration}%` }}
              />
            </div>
          </div>
        );
      })}

      <a href="/roadmap" className="text-sm text-[var(--clr-brand-primary)] hover:underline flex items-center gap-1 justify-center pt-2">
        <ExternalLink className="w-3 h-3" />
        Open Full Roadmap
      </a>
    </div>
  );
};

// Audit Log Tab (PM-specific) - Now fetches real data from backend
const AuditLogTab = ({ data, details, type }) => {
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAuditLog = async () => {
      if (!data?.id || !type) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const res = await api.getItemAuditLog(type, data.id, { limit: 20 });
        const logs = res.data?.audit_log || [];
        
        // Transform backend format to display format
        const formattedLogs = logs.map(log => ({
          id: log.id,
          action: log.action || 'Updated',
          from: log.old_value,
          to: log.new_value,
          user: log.user_name || 'System',
          timestamp: log.timestamp,
          field: log.field_changed
        }));
        
        // If no real logs, show helpful message (no mock data)
        setAuditLog(formattedLogs);
      } catch (err) {
        console.error('Failed to load audit log:', err);
        setError('Failed to load audit history');
        setAuditLog([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditLog();
  }, [data?.id, type]);

  const getActionColor = (action) => {
    if (action?.includes('Status') || action?.includes('status')) return 'text-[var(--clr-sev-atrisk)] bg-[var(--clr-sev-atrisk)] bg-opacity-10';
    if (action?.includes('Due') || action?.includes('date')) return 'text-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)] bg-opacity-10';
    if (action?.includes('Owner') || action?.includes('assign')) return 'text-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)] bg-opacity-10';
    if (action?.includes('Created') || action?.includes('created')) return 'text-[var(--clr-sev-healthy)] bg-[var(--clr-sev-healthy)] bg-opacity-10';
    if (action?.includes('Updated') || action?.includes('updated')) return 'text-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)] bg-opacity-10';
    return 'text-[var(--clr-text-secondary)] bg-[var(--clr-surface-page)]';
  };

  const getActionDotColor = (action) => {
    if (action?.includes('Status') || action?.includes('status')) return 'bg-[var(--clr-sev-atrisk)]';
    if (action?.includes('Due') || action?.includes('date')) return 'bg-[var(--clr-sev-inprogress)]';
    if (action?.includes('Owner') || action?.includes('assign')) return 'bg-[var(--clr-brand-primary)]';
    if (action?.includes('Created') || action?.includes('created')) return 'bg-[var(--clr-sev-healthy)]';
    if (action?.includes('Updated') || action?.includes('updated')) return 'bg-[var(--clr-brand-primary)]';
    return 'bg-[var(--clr-sev-notstarted)]';
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--clr-brand-primary)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-[var(--clr-text-muted)] mb-4">
        <History className="w-4 h-4" />
        <span>Recent activity on this item</span>
      </div>

      {error && (
        <div className="text-center py-4 text-[var(--clr-sev-blocked)] text-sm">
          {error}
        </div>
      )}

      {auditLog.length === 0 && !error ? (
        <div className="text-center py-8 text-[var(--clr-text-muted)]">
          <History className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No audit history available yet</p>
          <p className="text-xs text-[var(--clr-text-muted)] mt-1">Changes to this item will appear here</p>
        </div>
      ) : (
        <div className="relative">
          <div className="absolute top-2 left-4 bottom-2 w-0.5 bg-[var(--clr-border-light)]" />
          
          <div className="space-y-4">
            {auditLog.map((entry, idx) => (
              <div key={entry.id || idx} className="relative pl-10">
                <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 border-white ${getActionDotColor(entry.action)}`} />
                
                <div className="bg-[var(--clr-surface-page)] rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getActionColor(entry.action)}`}>
                      {entry.field ? `${entry.action}: ${entry.field}` : entry.action}
                    </span>
                    <span className="text-xs text-[var(--clr-text-muted)]">{formatTimestamp(entry.timestamp)}</span>
                  </div>
                  
                  <div className="text-sm text-[var(--clr-text-secondary)]">
                    {entry.from && entry.to && (
                      <span>{entry.from} → {entry.to}</span>
                    )}
                    {!entry.from && entry.to && (
                      <span>Set to: {entry.to}</span>
                    )}
                    {!entry.from && !entry.to && (entry.action?.includes('Created') || entry.action?.includes('created')) && (
                      <span>Item was created</span>
                    )}
                    {!entry.from && !entry.to && !entry.action?.includes('Created') && !entry.action?.includes('created') && (
                      <span className="text-[var(--clr-text-muted)] italic">No value change recorded</span>
                    )}
                  </div>
                  
                  <div className="text-xs text-[var(--clr-text-muted)] mt-1">
                    by {entry.user}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Comments Tab Component
const CommentsTab = ({ data, type, tenantId }) => {
  const entityType = (data?.type || type || 'work-item').toLowerCase();
  const entityId = data?.id;

  if (!entityId) {
    return (
      <div className="text-center py-8 text-[var(--clr-text-muted)]">
        <MessageCircle size={32} className="mx-auto mb-2 opacity-30" />
        <p>No entity selected</p>
      </div>
    );
  }

  return (
    <CommentsPanel
      entityType={entityType}
      entityId={entityId}
      tenantId={tenantId}
      compact={true}
    />
  );
};


// Scope Tab - Shows baseline linkage for work items generated from scope
const ScopeTab = ({ data, tenantId }) => {
  const navigate = useNavigate();
  
  if (!data?.scope_origin_id && !data?.baseline_version) {
    return (
      <div className="py-8 text-center text-[var(--clr-text-muted)]">
        <FileCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No scope linkage for this item</p>
      </div>
    );
  }
  
  const handleViewBaseline = () => {
    navigate(`/scope-baselines?project=${data.project_id || ''}`);
  };
  
  return (
    <div className="p-4 space-y-4" data-testid="scope-tab">
      {/* Baseline Version */}
      <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-[var(--clr-text-muted)] uppercase tracking-wider mb-1">Baseline Version</div>
            <div className="text-lg font-semibold text-[var(--clr-text-primary)]">
              V{data.baseline_version || '1'}
            </div>
          </div>
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <FileCheck className="w-5 h-5 text-green-600" />
          </div>
        </div>
      </div>
      
      {/* Scope Origin */}
      {data.scope_origin_id && (
        <div className="bg-[var(--clr-surface-card)] border border-[var(--clr-border-light)] rounded-lg p-4">
          <div className="text-xs text-[var(--clr-text-muted)] uppercase tracking-wider mb-2">Scope Origin</div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-purple-100 flex items-center justify-center">
              <Layers className="w-4 h-4 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[var(--clr-text-primary)] truncate">
                {data.scope_origin_name || data.scope_origin_id}
              </div>
              <div className="text-xs text-[var(--clr-text-muted)]">
                {data.source === 'scope_baseline' ? 'Generated from Scope Baseline' : 'Linked to Scope'}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Source Badge */}
      {data.source === 'scope_baseline' && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <FileCheck className="w-4 h-4 text-blue-600" />
          <span className="text-sm text-blue-700">Auto-generated from Scope Baseline</span>
        </div>
      )}
      
      {/* Normalized Requirement Text (if available) */}
      {data.scope_requirement_text && (
        <div className="bg-[var(--clr-surface-card)] border border-[var(--clr-border-light)] rounded-lg p-4">
          <div className="text-xs text-[var(--clr-text-muted)] uppercase tracking-wider mb-2">
            Original Requirement
          </div>
          <div className="text-sm text-[var(--clr-text-secondary)] bg-[var(--clr-surface-subtle)] p-3 rounded border border-[var(--clr-border-light)] italic">
            "{data.scope_requirement_text}"
          </div>
        </div>
      )}
      
      {/* Deep-link to Scope Baselines */}
      <button
        onClick={handleViewBaseline}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[var(--clr-surface-card)] border border-[var(--clr-border-light)] rounded-lg hover:bg-[var(--clr-surface-hover)] transition-colors text-sm font-medium text-[var(--clr-text-primary)]"
        data-testid="view-in-baselines-btn"
      >
        <ExternalLink className="w-4 h-4" />
        View in Scope Baselines
      </button>
    </div>
  );
};

// Scoping Mode Unavailable Panel - shown when entity type isn't allowed
const ScopingModeUnavailablePanel = ({ entityType, openSupportDrawer }) => {
  const formatEntityType = (type) => {
    const typeMap = {
      'risk': 'Risks',
      'issue': 'Issues',
      'dependency': 'Dependencies',
      'decision': 'Decisions',
      'epic': 'Epics',
      'feature': 'Features',
      'story': 'Stories',
      'task': 'Tasks',
      'program': 'Programs',
      'project': 'Projects',
      'portfolio': 'Portfolios',
      'work-item': 'Work Items',
    };
    return typeMap[type?.toLowerCase()] || type;
  };

  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center" data-testid="scoping-unavailable-panel">
      <div className="w-16 h-16 rounded-full bg-[var(--clr-surface-subtle)] flex items-center justify-center mb-4">
        <Info className="w-8 h-8 text-[var(--clr-text-muted)]" />
      </div>
      <h3 className="text-lg font-semibold text-[var(--clr-text-primary)] mb-2">
        Not available in Scoping Mode
      </h3>
      <p className="text-sm text-[var(--clr-text-secondary)] mb-6 max-w-xs">
        {formatEntityType(entityType)} information belongs to modules that are disabled in ScopeOps Scoping Mode.
      </p>
      <button
        onClick={() => openSupportDrawer?.({ 
          tab: 'product',
          initialMessage: `Help me understand what's available in Scoping Mode for ${formatEntityType(entityType)}.`
        })}
        className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] transition-colors text-sm font-medium"
        data-testid="ask-support-ai-unavailable"
      >
        <Sparkles className="w-4 h-4" />
        Ask Support AI
      </button>
    </div>
  );
};

// Support AI Tab - quick access to assistant in Inspector
const SupportAITab = ({ entityType, entityId, data, openSupportDrawer }) => {
  const contextMessage = data?.name 
    ? `Tell me more about "${data.name}" (${entityType})` 
    : `Help me with this ${entityType}`;

  return (
    <div className="space-y-4" data-testid="support-ai-tab">
      <div className="p-4 bg-gradient-to-br from-[var(--clr-brand-primary)]/5 to-[var(--clr-brand-primary)]/10 rounded-xl border border-[var(--clr-brand-primary)]/20">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[var(--clr-brand-primary)] flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-[var(--clr-text-primary)] mb-1">
              ScopeOps Support AI
            </h4>
            <p className="text-sm text-[var(--clr-text-secondary)]">
              Get instant help with scoping tasks, baseline comparisons, and coverage tracking.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium text-[var(--clr-text-muted)] uppercase tracking-wider">
          Quick Actions
        </p>
        <button
          onClick={() => openSupportDrawer?.({ tab: 'advisory', initialMessage: contextMessage })}
          className="w-full flex items-center gap-3 p-3 bg-[var(--clr-surface-card)] border border-[var(--clr-border-light)] rounded-lg hover:bg-[var(--clr-surface-hover)] transition-colors text-left"
          data-testid="ask-about-item"
        >
          <MessageCircleQuestion className="w-5 h-5 text-[var(--clr-brand-primary)]" />
          <span className="text-sm text-[var(--clr-text-primary)]">Ask about this item</span>
        </button>
        <button
          onClick={() => openSupportDrawer?.({ tab: 'product', initialMessage: 'How do I compare scope baselines?' })}
          className="w-full flex items-center gap-3 p-3 bg-[var(--clr-surface-card)] border border-[var(--clr-border-light)] rounded-lg hover:bg-[var(--clr-surface-hover)] transition-colors text-left"
          data-testid="help-baseline-compare"
        >
          <GitBranch className="w-5 h-5 text-[var(--clr-text-muted)]" />
          <span className="text-sm text-[var(--clr-text-primary)]">Compare baselines</span>
        </button>
        <button
          onClick={() => openSupportDrawer?.({ tab: 'product', initialMessage: 'How do I track scope coverage?' })}
          className="w-full flex items-center gap-3 p-3 bg-[var(--clr-surface-card)] border border-[var(--clr-border-light)] rounded-lg hover:bg-[var(--clr-surface-hover)] transition-colors text-left"
          data-testid="help-coverage"
        >
          <Target className="w-5 h-5 text-[var(--clr-text-muted)]" />
          <span className="text-sm text-[var(--clr-text-primary)]">Track coverage</span>
        </button>
      </div>
    </div>
  );
};

export default DetailDrawerV2;
